import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Church,
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Gem,
  Gift,
  Palette,
  Camera,
  Crown,
  Flower2,
  Cross,
  BookOpen,
  User
} from 'lucide-react';
import { toast } from 'sonner';

// Product categories
const PRODUCT_CATEGORIES = [
  { value: 'bomboniere', label: 'Bomboniere', icon: Gift },
  { value: 'inviti', label: 'Inviti e partecipazioni', icon: MessageSquare },
  { value: 'confettata', label: 'Confettata / Sweet table', icon: Gift },
  { value: 'centrotavola', label: 'Centrotavola', icon: Flower2 },
  { value: 'decorazioni', label: 'Decorazioni chiesa/sala', icon: Sparkles },
  { value: 'album', label: 'Album e libri firma', icon: Camera },
  { value: 'rosario', label: 'Rosari e oggetti sacri', icon: Cross },
  { value: 'tableau', label: 'Tableau e segnaposto', icon: Palette },
  { value: 'torta', label: 'Torta personalizzata', icon: Star },
  { value: 'abbigliamento', label: 'Abito comunione / Accessori', icon: Crown },
  { value: 'pacchetto', label: 'Pacchetto completo', icon: Crown },
  { value: 'altro', label: 'Altro', icon: Sparkles },
];

// Themes
const THEMES = [
  { value: 'classico', label: 'Classico / Tradizionale', icon: Church },
  { value: 'elegante', label: 'Elegante / Raffinato', icon: Gem },
  { value: 'romantico', label: 'Romantico / Fiori', icon: Flower2 },
  { value: 'moderno', label: 'Moderno / Minimal', icon: Zap },
  { value: 'rustico', label: 'Rustico / Country', icon: Heart },
  { value: 'marino', label: 'Marino / Nautico', icon: Star },
  { value: 'angeli', label: 'Angeli e simboli sacri', icon: Sparkles },
  { value: 'naturale', label: 'Naturale / Eco', icon: Flower2 },
  { value: 'personalizzato', label: 'Tema personalizzato', icon: Palette },
];

// Colors
const COLOR_SCHEMES = [
  { value: 'bianco_oro', label: 'Bianco e oro', icon: Crown },
  { value: 'bianco_argento', label: 'Bianco e argento', icon: Gem },
  { value: 'bianco_rosa', label: 'Bianco e rosa', icon: Heart },
  { value: 'bianco_azzurro', label: 'Bianco e azzurro', icon: Star },
  { value: 'avorio_verde', label: 'Avorio e verde', icon: Flower2 },
  { value: 'panna_tortora', label: 'Panna e tortora', icon: Heart },
  { value: 'pastello', label: 'Colori pastello', icon: Sparkles },
  { value: 'personalizzato', label: 'Colori personalizzati', icon: Palette },
];

// Guest counts
const GUEST_COUNTS = [
  { value: '20-40', label: '20-40 invitati' },
  { value: '40-70', label: '40-70 invitati' },
  { value: '70-100', label: '70-100 invitati' },
  { value: '100-150', label: '100-150 invitati' },
  { value: '150+', label: '150+ invitati' },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '150-350', label: '€150 - €350', description: 'Bomboniere base' },
  { value: '350-600', label: '€350 - €600', description: 'Allestimento semplice' },
  { value: '600-1000', label: '€600 - €1000', description: 'Pacchetto curato' },
  { value: '1000-1500', label: '€1000 - €1500', description: 'Evento elaborato' },
  { value: '1500-2500', label: '€1500 - €2500', description: 'Premium' },
  { value: '2500+', label: '€2500+', description: 'Esclusivo' },
];

// Timeline
const TIMELINE_OPTIONS = [
  { value: '1month', label: '1 Mese', description: 'Urgente' },
  { value: '2months', label: '2 Mesi', description: 'Prioritario' },
  { value: '3months', label: '3 Mesi', description: 'Standard' },
  { value: '4-6months', label: '4-6 Mesi', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Data non definita' },
];

// Examples
const COMMUNION_EXAMPLES = [
  {
    idea: "Bomboniere comunione eleganti ma non banali",
    result: "Scatoline in legno inciso con nome e data, interno foderato in velluto, confetti artigianali",
    image: "https://images.unsplash.com/photo-1746559658037-241ce116cef0?w=400&h=300&fit=crop",
    category: "Bomboniere"
  },
  {
    idea: "Inviti Prima Comunione con calligrafia",
    result: "Partecipazioni in carta pregiata con bordo oro, calligrafia a mano del nome, busta personalizzata",
    image: "https://images.pexels.com/photos/11503482/pexels-photo-11503482.jpeg?w=400&h=300&fit=crop",
    category: "Inviti"
  },
  {
    idea: "Centrotavola eleganti per il ricevimento",
    result: "Composizioni con fiori freschi bianchi, candele, tocchi dorati, personalizzate per ogni tavolo",
    image: "https://images.unsplash.com/photo-1769812343775-85a27e6a076c?w=400&h=300&fit=crop",
    category: "Centrotavola"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "La Prima Comunione di Matteo è stata perfetta! Le bomboniere in ceramica con l'angelo erano bellissime, ogni invitato ha apprezzato la qualità artigianale.",
    author: "Laura B.",
    location: "Milano"
  },
  {
    text: "Volevo qualcosa di diverso dal solito. L'artigiana ha creato dei libretti in pelle con preghiere calligrafate. Un ricordo che Giulia conserverà per sempre.",
    author: "Paola R.",
    location: "Roma"
  },
  {
    text: "Allestimento chiesa e sala coordinati perfettamente. Dai fiori alle bomboniere, tutto era in tema e di una qualità superiore. Complimenti!",
    author: "Marco T.",
    location: "Firenze"
  }
];

// FAQ
const FAQ_ITEMS = [
  {
    question: "Qual è la differenza tra bomboniere per comunione e cresima?",
    answer: "Tradizionalmente, la comunione ha simboli come calice, ostia, spighe e uva. La cresima usa simboli come la colomba e le fiamme dello Spirito Santo. Ma oggi molti preferiscono bomboniere eleganti senza simboli religiosi specifici."
  },
  {
    question: "Posso avere lo stesso stile per bomboniere e inviti?",
    answer: "Certamente! Molti artigiani offrono coordinati completi: inviti, bomboniere, tableau, segnaposto, tutti con lo stesso tema, colori e stile grafico."
  },
  {
    question: "Che bomboniere vanno di più per la comunione?",
    answer: "Le più richieste sono: ceramiche dipinte, scatoline in legno inciso, sacchettini ricamati, oggetti in vetro soffiato, candele profumate, piccoli gioielli. L'artigiano saprà consigliarti."
  },
  {
    question: "Offrite anche addobbi per la chiesa?",
    answer: "Sì, alcuni artigiani si occupano anche di composizioni floreali per la chiesa, cuscini portafedi, libretti messa personalizzati e altri addobbi per la cerimonia."
  }
];

export default function ComunioniRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    child_name: '',
    ceremony_type: 'comunione',
    product_category: '',
    theme: '',
    color_scheme: '',
    guest_count: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (formData.attachments.length + files.length > 5) { toast.error('Max 5 immagini'); return; }
    setUploadingFile(true);
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 5 * 1024 * 1024) continue;
      try {
        const fileName = `communion-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const { error } = await supabase.storage.from('rarifex-uploads').upload(`requests/${fileName}`, file);
        if (error) throw error;
        const { data: { publicUrl } } = supabase.storage.from('rarifex-uploads').getPublicUrl(`requests/${fileName}`);
        setFormData(prev => ({ ...prev, attachments: [...prev.attachments, publicUrl] }));
      } catch (error) { toast.error('Errore upload'); }
    }
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) { toast.error('Descrivi cosa cerchi'); return; }
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_communion_request', JSON.stringify(formData));
      return;
    }
    setLoading(true);
    try {
      const ceremonyLabel = formData.ceremony_type === 'comunione' ? 'Prima Comunione' : 'Cresima';
      const productLabel = PRODUCT_CATEGORIES.find(t => t.value === formData.product_category)?.label || '';
      const themeLabel = THEMES.find(t => t.value === formData.theme)?.label || '';
      const colorLabel = COLOR_SCHEMES.find(t => t.value === formData.color_scheme)?.label || '';
      const guestLabel = GUEST_COUNTS.find(t => t.value === formData.guest_count)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      const details = [];
      if (formData.child_name) details.push(`👦 Nome: ${formData.child_name}`);
      details.push(`⛪ Cerimonia: ${ceremonyLabel}`);
      if (productLabel) details.push(`🎁 Cerco: ${productLabel}`);
      if (themeLabel) details.push(`✨ Tema: ${themeLabel}`);
      if (colorLabel) details.push(`🎨 Colori: ${colorLabel}`);
      if (guestLabel) details.push(`👥 Invitati: ${guestLabel}`);
      if (budgetLabel) details.push(`💰 Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️ Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        fullDescription += `\n\n━━━━━━━━━━━━━━━━━━━━━━━\n⛪ DETTAGLI ${ceremonyLabel.toUpperCase()}\n━━━━━━━━━━━━━━━━━━━━━━━\n` + details.join('\n');
      }
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: user.id,
          title: formData.title || `${ceremonyLabel} ${formData.child_name || ''} - ${productLabel}`.trim(),
          description: fullDescription,
          category: 'Comunioni',
          price_range: formData.budget || 'flexible',
          timeline: formData.timeline || 'flexible',
          attachments: formData.attachments,
        }),
      });
      if (!response.ok) throw new Error('Errore');
      toast.success('Richiesta inviata!');
      navigate('/requests');
    } catch (error) { toast.error('Errore nell\'invio'); }
    finally { setLoading(false); }
  };

  const scrollToForm = () => document.getElementById('communion-form')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <>
      <Helmet>
        <title>Comunione e Cresima Su Misura | Bomboniere Artigianali | Rarifex</title>
        <meta name="description" content="Organizza la Prima Comunione o Cresima perfetta con bomboniere e inviti artigianali. Ceramiche, legno inciso, calligrafia. Made in Italy." />
        <meta name="keywords" content="comunione, cresima, bomboniere comunione, inviti comunione, bomboniere cresima, prima comunione, bomboniere artigianali" />
        <link rel="canonical" href="https://www.rarifex.com/comunioni-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-amber-50/50 via-white to-yellow-50/30">
        
        {/* HERO */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-100/50 via-transparent to-transparent" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Church className="w-4 h-4" />
                    Comunione & Cresima
                  </div>
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Un giorno{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-yellow-600">
                      da ricordare
                    </span>
                  </h1>
                  <p className="text-xl text-slate-600 mb-8">
                    Bomboniere, inviti e decorazioni artigianali per la Prima Comunione o Cresima.
                  </p>
                  <p className="text-lg text-slate-500 mb-8">
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500" /><span>Artigiani verificati</span></div>
                    <div className="flex items-center gap-2"><Clock className="w-5 h-5 text-amber-500" /><span>Risposte in 24-48h</span></div>
                    <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-slate-500" /><span>Made in Italy</span></div>
                  </div>
                </div>
                
                <div id="communion-form">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Nome</Label>
                          <Input placeholder="Es: Matteo" value={formData.child_name} onChange={(e) => handleInputChange('child_name', e.target.value)} className="mt-1.5" data-testid="communion-name" />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Cerimonia</Label>
                          <select value={formData.ceremony_type} onChange={(e) => handleInputChange('ceremony_type', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="communion-type">
                            <option value="comunione">Prima Comunione</option>
                            <option value="cresima">Cresima</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Descrivi cosa cerchi *</Label>
                        <Textarea placeholder="Racconta la tua idea... Stile, atmosfera, dettagli speciali." value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} className="mt-1.5 min-h-[100px]" required data-testid="communion-description" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Cosa cerchi?</Label>
                          <select value={formData.product_category} onChange={(e) => handleInputChange('product_category', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="communion-product">
                            <option value="">Seleziona...</option>
                            {PRODUCT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tema</Label>
                          <select value={formData.theme} onChange={(e) => handleInputChange('theme', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="communion-theme">
                            <option value="">Seleziona...</option>
                            {THEMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Colori</Label>
                          <select value={formData.color_scheme} onChange={(e) => handleInputChange('color_scheme', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="communion-colors">
                            <option value="">Seleziona...</option>
                            {COLOR_SCHEMES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Invitati</Label>
                          <select value={formData.guest_count} onChange={(e) => handleInputChange('guest_count', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="communion-guests">
                            <option value="">Seleziona...</option>
                            {GUEST_COUNTS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select value={formData.budget} onChange={(e) => handleInputChange('budget', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="communion-budget">
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Quando?</Label>
                          <select value={formData.timeline} onChange={(e) => handleInputChange('timeline', e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="communion-timeline">
                            <option value="">Seleziona...</option>
                            {TIMELINE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Foto ispirazione</Label>
                        <div className="mt-1.5">
                          {formData.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={url} alt="" className="w-14 h-14 object-cover rounded-lg" />
                                  <button type="button" onClick={() => removeAttachment(idx)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                                </div>
                              ))}
                            </div>
                          )}
                          {formData.attachments.length < 5 && (
                            <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-amber-300">
                              <input type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" disabled={uploadingFile} />
                              {uploadingFile ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5 text-slate-400" />}
                              <span className="text-sm text-slate-500">Carica ({formData.attachments.length}/5)</span>
                            </label>
                          )}
                        </div>
                      </div>
                      <Button type="submit" disabled={loading} className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700" data-testid="communion-submit">
                        {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Church className="w-5 h-5 mr-2" />}
                        Ricevi proposte gratis
                      </Button>
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ESEMPI */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Ecco cosa creano i nostri artigiani</h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {COMMUNION_EXAMPLES.map((ex, idx) => (
                <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <img src={ex.image} alt={ex.result} className="w-full h-48 object-cover" />
                  <div className="p-5">
                    <span className="text-xs font-medium text-amber-600 uppercase">{ex.category}</span>
                    <p className="text-sm text-slate-500 mt-2">"{ex.idea}"</p>
                    <p className="font-semibold text-slate-800 mt-1">→ {ex.result}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 bg-amber-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Cosa dicono i genitori</h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {TESTIMONIALS.map((t, idx) => (
                <Card key={idx} className="p-6 bg-white">
                  <div className="flex gap-1 mb-4">{[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />)}</div>
                  <p className="text-slate-600 italic mb-4">"{t.text}"</p>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center font-semibold text-amber-700">{t.author[0]}</div>
                    <div><p className="font-medium">{t.author}</p><p className="text-slate-500 text-xs">{t.location}</p></div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl font-bold text-center mb-12">Domande frequenti</h2>
            <div className="space-y-4">
              {FAQ_ITEMS.map((faq, idx) => (
                <div key={idx} className="border rounded-xl overflow-hidden">
                  <button onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)} className="w-full flex items-center justify-between p-5 text-left hover:bg-amber-50">
                    <span className="font-medium">{faq.question}</span>
                    <ChevronRight className={`w-5 h-5 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                  </button>
                  {expandedFaq === idx && <div className="px-5 pb-5 text-slate-600">{faq.answer}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-b from-amber-100 to-amber-50">
          <div className="container mx-auto px-4 text-center">
            <Church className="w-16 h-16 text-amber-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">Pronto a organizzare la cerimonia perfetta?</h2>
            <p className="text-xl text-slate-600 mb-8">Gli artigiani italiani aspettano la tua richiesta.</p>
            <Button onClick={scrollToForm} size="lg" className="py-6 px-10 text-lg bg-gradient-to-r from-amber-500 to-yellow-600" data-testid="communion-cta-final">
              <Church className="w-5 h-5 mr-2" /> Ricevi proposte gratis
            </Button>
          </div>
        </section>
      </div>

      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Accedi per continuare</h3>
            <p className="text-slate-600 mb-6">Per inviare la richiesta devi accedere.</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowAuthPrompt(false)} className="flex-1">Annulla</Button>
              <Button onClick={() => navigate('/login?redirect=/comunioni-su-misura')} className="flex-1 bg-amber-500">Accedi</Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
